import { addMonths } from "date-fns";
import { Trash2 } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { DatePickerInput } from "@/shared/components/ui/date-picker-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { SystemDictionary } from "@/shared/api/filters";
import type { PassengerFormState } from "./CreateOrderModal";

const COUNTRY_NAMES: Record<string, string> = {
  KZ: "Казахстан",
  RU: "Россия",
  US: "США",
  DE: "Германия",
  GB: "Великобритания",
  FR: "Франция",
  IT: "Италия",
  ES: "Испания",
  TR: "Турция",
  AE: "ОАЭ",
  TH: "Таиланд",
  IN: "Индия",
  CN: "Китай",
  EG: "Египет",
  GR: "Греция",
  CZ: "Чехия",
  PL: "Польша",
  UA: "Украина",
  BY: "Беларусь",
  UZ: "Узбекистан",
  KG: "Киргизия",
  TJ: "Таджикистан",
  TM: "Туркменистан",
  AZ: "Азербайджан",
  AM: "Армения",
  GE: "Грузия",
  IL: "Израиль",
  JP: "Япония",
  KR: "Южная Корея",
  SG: "Сингапур",
  MY: "Малайзия",
  ID: "Индонезия",
  VN: "Вьетнам",
  PH: "Филиппины",
  AU: "Австралия",
  NZ: "Новая Зеландия",
  CA: "Канада",
  MX: "Мексика",
  BR: "Бразилия",
  AR: "Аргентина",
  ZA: "ЮАР",
  MA: "Марокко",
  TN: "Тунис",
  AT: "Австрия",
  CH: "Швейцария",
  NL: "Нидерланды",
  BE: "Бельгия",
  SE: "Швеция",
  NO: "Норвегия",
  FI: "Финляндия",
  DK: "Дания",
  PT: "Португалия",
  HU: "Венгрия",
  RO: "Румыния",
  BG: "Болгария",
  HR: "Хорватия",
  RS: "Сербия",
  SK: "Словакия",
  SI: "Словения",
  LT: "Литва",
  LV: "Латвия",
  EE: "Эстония",
  MN: "Монголия",
};

interface PassengerInfoFormProps {
  index: number;
  state: PassengerFormState;
  errors: Record<string, string>;
  onChange: (updates: Partial<PassengerFormState>) => void;
  onDelete?: () => void;
  dictionary: SystemDictionary;
  arrivalDate?: Date;
}

export function PassengerInfoForm({
  index,
  state,
  errors,
  onChange,
  onDelete,
  dictionary,
  arrivalDate,
}: PassengerInfoFormProps) {
  const isKZ = state.issuingCountry === "KZ";
  const pfx = `passenger_${index}_`;

  const handleCountryChange = (code: string) => {
    if (code === "KZ") {
      onChange({ issuingCountry: code, serialNumber: "" });
    } else {
      onChange({ issuingCountry: code, iin: "" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          Пассажир {index + 1}
        </h3>
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Имя</Label>
          <Input
            value={state.name}
            onChange={(e) =>
              onChange({ name: e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase() })
            }
            placeholder="IVAN"
          />
          {errors[`${pfx}name`] && <p className="text-xs text-destructive">{errors[`${pfx}name`]}</p>}
        </div>

        <div className="space-y-1">
          <Label>Фамилия</Label>
          <Input
            value={state.surname}
            onChange={(e) =>
              onChange({ surname: e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase() })
            }
            placeholder="PETROV"
          />
          {errors[`${pfx}surname`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}surname`]}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Гражданство</Label>
          <Select value={state.issuingCountry} onValueChange={handleCountryChange}>
            <SelectTrigger>
              <SelectValue placeholder="Выберите страну" />
            </SelectTrigger>
            <SelectContent>
              {(dictionary.countries?.length
                ? dictionary.countries
                : Object.keys(COUNTRY_NAMES)
              ).map((code) => (
                <SelectItem key={code} value={code}>
                  {COUNTRY_NAMES[code] ?? code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors[`${pfx}issuingCountry`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}issuingCountry`]}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Пол</Label>
          <Select
            value={state.gender}
            onValueChange={(v) => onChange({ gender: v as "male" | "female" })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Выберите пол" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Мужской</SelectItem>
              <SelectItem value="female">Женский</SelectItem>
            </SelectContent>
          </Select>
          {errors[`${pfx}gender`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}gender`]}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Дата рождения</Label>
          <DatePickerInput
            value={state.birthday}
            onSelect={(d) => onChange({ birthday: d })}
            disabled={(date) => date >= new Date()}
            fromYear={1900}
            toYear={new Date().getFullYear()}
          />
          {errors[`${pfx}birthday`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}birthday`]}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Номер документа</Label>
          <Input
            value={state.documentId}
            onChange={(e) =>
              onChange({ documentId: e.target.value.toUpperCase() })
            }
            placeholder="N12345678"
            className={errors[`${pfx}documentId`] ? "border-destructive" : undefined}
          />
          {errors[`${pfx}documentId`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}documentId`]}</p>
          )}
        </div>

        {isKZ ? (
          <div className="space-y-1">
            <Label>ИИН</Label>
            <Input
              value={state.iin}
              onChange={(e) =>
                onChange({ iin: e.target.value.replace(/\D/g, "").slice(0, 12) })
              }
              placeholder="123456789012"
              maxLength={12}
            />
            {errors[`${pfx}iin`] && <p className="text-xs text-destructive">{errors[`${pfx}iin`]}</p>}
          </div>
        ) : (
          <div className="space-y-1">
            <Label>Серия паспорта</Label>
            <Input
              value={state.serialNumber}
              onChange={(e) => onChange({ serialNumber: e.target.value })}
              placeholder="AB"
            />
            {errors[`${pfx}serialNumber`] && (
              <p className="text-xs text-destructive">{errors[`${pfx}serialNumber`]}</p>
            )}
          </div>
        )}

        <div className="space-y-1 col-span-2">
          <Label>Кем выдано</Label>
          <Input
            value={state.issuedBy}
            onChange={(e) =>
              onChange({ issuedBy: e.target.value.replace(/[^A-Za-z ]/g, "").toUpperCase() })
            }
            placeholder="MVD RK"
            maxLength={50}
          />
          {errors[`${pfx}issuedBy`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}issuedBy`]}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Дата выдачи документа</Label>
          <DatePickerInput
            value={state.issueDate}
            onSelect={(d) => onChange({ issueDate: d })}
            fromYear={1900}
            toYear={new Date().getFullYear() + 20}
          />
          {errors[`${pfx}issueDate`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}issueDate`]}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Срок действия документа</Label>
          <DatePickerInput
            value={state.documentExpiredAt}
            onSelect={(d) => onChange({ documentExpiredAt: d })}
            fromYear={1900}
            toYear={new Date().getFullYear() + 20}
            disabled={
              arrivalDate
                ? (date: Date) => date < addMonths(arrivalDate, 6)
                : (date: Date) => date <= new Date()
            }
          />
          {errors[`${pfx}documentExpiredAt`] && (
            <p className="text-xs text-destructive">{errors[`${pfx}documentExpiredAt`]}</p>
          )}
        </div>
      </div>
    </div>
  );
}
